export interface ImageFile {
  id: string;
  file: File;
  preview?: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  processedFile?: File;
  resizedBlob?: Blob;
  originalSize: { width: number; height: number };
  targetSize?: { width: number; height: number };
  fileType: string;
  compressionRatio?: number;
  outputSize?: number;
  processedAt?: Date;
  individualSettings?: {
    format: string;
    quality: number;
    width?: number;
    height?: number;
  };
}

export interface VideoFile {
  id: string;
  file: File;
  preview?: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  processedFile?: File;
  originalDuration?: number;
  fileType: string;
}

export interface DocumentFile {
  id: string;
  file: File;
  preview?: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  processedFile?: File;
  fileType: string;
  pageCount?: number;
  originalFormat: string;
}

export interface ResizeSettings {
  width: number;
  height: number;
  quality: number;
  format: string;
  maintainAspectRatio: boolean;
  removeBackground: boolean;
}

export interface VideoSettings {
  format: string;
  quality: 'low' | 'medium' | 'high' | 'ultra';
  resolution?: string;
  bitrate?: number;
}

export interface DocumentSettings {
  format: string;
  quality: 'low' | 'medium' | 'high' | 'maximum';
  preserveMetadata: boolean;
  optimizeForWeb: boolean;
}

export interface ProcessingProgress {
  current: number;
  total: number;
  currentFileName?: string;
  estimatedTimeRemaining?: number;
  processingSpeed?: number;
}

export interface ConversionPreset {
  name: string;
  width: number;
  height: number;
  quality: number;
  format: string;
  description: string;
}

export interface ConversionHistory {
  id: string;
  originalFile: string;
  convertedFile: string;
  settings: ResizeSettings;
  timestamp: Date;
  compressionRatio: number;
}

export type AppTheme = 'light' | 'dark' | 'auto';

export type PageType = 'home' | 'images' | 'videos' | 'documents' | 'copywriter';

export interface NavigationItem {
  id: PageType;
  title: string;
  description: string;
  icon: string;
  path: string;
  iconColor: string;
  activeColor: string;
  weight: 'regular' | 'bold' | 'fill' | 'light' | 'thin' | 'duotone';
}

export interface UserPreferences {
  theme: AppTheme;
  defaultQuality: number;
  autoProcess: boolean;
  preserveMetadata: boolean;
}

// Formatos suportados expandidos
export const SUPPORTED_IMAGE_FORMATS = [
  // Formatos comuns
  'png', 'jpeg', 'jpg', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'svg',
  
  // Formatos avançados
  'ico', 'hdr', 'psd', 'dds', 'avif', 'heic', 'heif', 'cur', 'rgb',
  'wbmp', 'tga', 'jbg', 'jp2', 'xpm', 'jfif', 'jpe', 'exr', 'pcx',
  'pgm', 'ppm', 'map', 'pdb', 'jif', 'g3', 'xbm', 'pbm', 'jps',
  'rgba', 'ras', 'mtv', 'pic', 'picon', 'jbig', 'sixel', 'fax',
  'sgi', 'pct', 'yuv', 'pcd', 'g4', 'vips', 'pfm', 'jfi', 'pgx',
  'six', 'pnm', 'ipl', 'pal', 'rgf', 'mng', 'xv', 'xwd', 'otb',
  'pam', 'viff', 'sun', 'uyvy', 'hrz', 'rgbo', 'palm', 'fts'
] as const;

export const SUPPORTED_VIDEO_FORMATS = [
  'mp4', 'webm', 'ogv', 'm4v', 'mov'
] as const;

export const SUPPORTED_DOCUMENT_FORMATS = [
  'doc', 'pdf', 'docx', 'docm', 'odt', 'dot', 'xps', 'rtf', 'dotx', 
  'djvu', 'sxw', 'aw', 'dotm', 'oxps', 'abw', 'kwd', 'dbk'
] as const;

// Categorias de formatos
export const FORMAT_CATEGORIES = {
  raster: ['png', 'jpeg', 'jpg', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'ico', 'hdr', 'dds', 'avif', 'heic', 'heif', 'cur', 'rgb', 'wbmp', 'tga', 'jbg', 'jp2', 'jfif', 'jpe', 'exr', 'pcx', 'pgm', 'ppm', 'map', 'pdb', 'jif', 'g3', 'xbm', 'pbm', 'jps', 'rgba', 'ras', 'mtv', 'pic', 'picon', 'jbig', 'sixel', 'fax', 'sgi', 'pct', 'yuv', 'pcd', 'g4', 'vips', 'pfm', 'jfi', 'pgx', 'six', 'pnm', 'ipl', 'pal', 'rgf', 'mng', 'xv', 'xwd', 'otb', 'pam', 'viff', 'sun', 'uyvy', 'hrz', 'rgbo', 'palm', 'fts'],
  vector: ['svg'],
  raw: ['psd', 'hdr', 'exr', 'rgb', 'rgba'],
  web: ['png', 'jpeg', 'jpg', 'gif', 'webp', 'svg', 'avif', 'heic', 'heif'],
  print: ['tiff', 'tif', 'bmp', 'png', 'jpeg', 'jpg'],
  legacy: ['bmp', 'ico', 'cur', 'pcx', 'tga', 'xbm', 'pbm', 'pgm', 'ppm'],
  specialized: ['dds', 'hdr', 'exr', 'vips', 'pfm', 'sixel', 'fax', 'g3', 'g4']
} as const;

// Descrições dos formatos
export const FORMAT_DESCRIPTIONS = {
  // Formatos populares
  'png': 'PNG - Portable Network Graphics (sem perda, transparência)',
  'jpeg': 'JPEG - Joint Photographic Experts Group (compressão)',
  'jpg': 'JPG - Joint Photographic Experts Group (compressão)',
  'gif': 'GIF - Graphics Interchange Format (animação)',
  'webp': 'WebP - Formato web moderno do Google',
  'svg': 'SVG - Scalable Vector Graphics (vetorial)',
  'bmp': 'BMP - Bitmap (Windows)',
  'tiff': 'TIFF - Tagged Image File Format (profissional)',
  'tif': 'TIF - Tagged Image File Format (profissional)',
  
  // Formatos modernos
  'avif': 'AVIF - AV1 Image File Format (nova geração)',
  'heic': 'HEIC - High Efficiency Image Container (Apple)',
  'heif': 'HEIF - High Efficiency Image Format',
  
  // Formatos especializados
  'ico': 'ICO - Ícone Windows',
  'cur': 'CUR - Cursor Windows',
  'hdr': 'HDR - High Dynamic Range',
  'exr': 'EXR - OpenEXR (cinema/VFX)',
  'psd': 'PSD - Photoshop Document',
  'dds': 'DDS - DirectDraw Surface (jogos)',
  
  // Formatos científicos/técnicos
  'rgb': 'RGB - Raw RGB Data',
  'rgba': 'RGBA - Raw RGBA Data',
  'yuv': 'YUV - Luminance/Chrominance',
  'vips': 'VIPS - Very Large Image Processing',
  'pfm': 'PFM - Portable Float Map',
  
  // Formatos legados
  'pcx': 'PCX - PiCture eXchange',
  'tga': 'TGA - Truevision Graphics Adapter',
  'xbm': 'XBM - X11 Bitmap',
  'xpm': 'XPM - X11 Pixmap',
  'pbm': 'PBM - Portable Bitmap',
  'pgm': 'PGM - Portable Graymap',
  'ppm': 'PPM - Portable Pixmap',
  'pnm': 'PNM - Portable Anymap',
  
  // Formatos especiais
  'sixel': 'SIXEL - DEC Terminal Graphics',
  'fax': 'FAX - Facsimile Image',
  'g3': 'G3 - CCITT Group 3 Fax',
  'g4': 'G4 - CCITT Group 4 Fax',
  'palm': 'PALM - Palm Pilot Image',
  'picon': 'PICON - Personal Icon',
  'sun': 'SUN - Sun Rasterfile',
  'sgi': 'SGI - Silicon Graphics Image',
  'ras': 'RAS - Sun Raster',
  'mtv': 'MTV - MTV Raytracer',
  'otb': 'OTB - On-the-air Bitmap',
  'pam': 'PAM - Portable Arbitrary Map',
  'viff': 'VIFF - Khoros Visualization',
  'xwd': 'XWD - X11 Window Dump',
  'wbmp': 'WBMP - Wireless Bitmap',
  'jbig': 'JBIG - Joint Bi-level Image Group',
  'jp2': 'JP2 - JPEG 2000',
  'jps': 'JPS - JPEG Stereo',
  'mng': 'MNG - Multiple-image Network Graphics',
  'pcd': 'PCD - Kodak Photo CD',
  'pct': 'PCT - Macintosh PICT',
  'pic': 'PIC - Generic Picture Format',
  'map': 'MAP - Colormap File',
  'pal': 'PAL - Color Palette',
  'rgf': 'RGF - LEGO Mindstorms',
  'ipl': 'IPL - Image Processing Library',
  'pgx': 'PGX - JPEG 2000 VM Format',
  'six': 'SIX - DEC SIXEL Format',
  'hrz': 'HRZ - Slow Scan TV',
  'rgbo': 'RGBO - RGB with Opacity',
  'uyvy': 'UYVY - YUV 4:2:2 Format',
  'fts': 'FTS - Flexible Image Transport System',
  'jfif': 'JFIF - JPEG File Interchange Format',
  'jpe': 'JPE - JPEG Extension',
  'jif': 'JIF - JPEG Interchange Format',
  'jfi': 'JFI - JPEG File Interchange',
  'pdb': 'PDB - Palm Database ImageViewer'
} as const;

// Funções utilitárias
export function validateFileType(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!extension) return false;
  
  return [...SUPPORTED_IMAGE_FORMATS, ...SUPPORTED_VIDEO_FORMATS, ...SUPPORTED_DOCUMENT_FORMATS]
    .includes(extension as any);
}

export function getFileCategory(extension: string): string {
  const ext = extension.toLowerCase();
  
  for (const [category, formats] of Object.entries(FORMAT_CATEGORIES)) {
    if ((formats as readonly string[]).includes(ext)) {
      return category;
    }
  }
  
  if (SUPPORTED_VIDEO_FORMATS.includes(ext as any)) return 'video';
  if (SUPPORTED_DOCUMENT_FORMATS.includes(ext as any)) return 'document';
  
  return 'unknown';
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function calculateCompressionRatio(originalSize: number, compressedSize: number): number {
  if (originalSize === 0) return 0;
  return Math.round(((originalSize - compressedSize) / originalSize) * 100);
}

// Presets de qualidade
export const QUALITY_PRESETS = {
  'maximum': { quality: 1.0, description: 'Qualidade máxima (sem compressão)' },
  'high': { quality: 0.95, description: 'Alta qualidade (compressão mínima)' },
  'medium': { quality: 0.85, description: 'Qualidade média (balanceada)' },
  'low': { quality: 0.7, description: 'Baixa qualidade (mais compressão)' },
  'web': { quality: 0.8, description: 'Otimizada para web' },
  'mobile': { quality: 0.75, description: 'Otimizada para mobile' }
} as const;

// Presets de conversão populares
export const CONVERSION_PRESETS: ConversionPreset[] = [
  // Redes sociais
  { name: 'Instagram Post', width: 1080, height: 1080, quality: 0.9, format: 'jpeg', description: 'Quadrado para feed' },
  { name: 'Instagram Story', width: 1080, height: 1920, quality: 0.9, format: 'jpeg', description: 'Vertical para stories' },
  { name: 'Facebook Cover', width: 1200, height: 630, quality: 0.9, format: 'jpeg', description: 'Capa do Facebook' },
  { name: 'Twitter Header', width: 1500, height: 500, quality: 0.9, format: 'jpeg', description: 'Cabeçalho do Twitter' },
  { name: 'LinkedIn Banner', width: 1584, height: 396, quality: 0.9, format: 'jpeg', description: 'Banner do LinkedIn' },
  
  // YouTube
  { name: 'YouTube Thumbnail', width: 1280, height: 720, quality: 0.95, format: 'jpeg', description: 'Miniatura do YouTube' },
  { name: 'YouTube Banner', width: 2560, height: 1440, quality: 0.95, format: 'jpeg', description: 'Banner do canal' },
  
  // Web
  { name: 'Web Hero', width: 1920, height: 1080, quality: 0.85, format: 'webp', description: 'Imagem principal do site' },
  { name: 'Web Thumbnail', width: 300, height: 200, quality: 0.8, format: 'webp', description: 'Miniatura para web' },
  { name: 'Blog Featured', width: 1200, height: 675, quality: 0.85, format: 'webp', description: 'Imagem destacada do blog' },
  
  // Impressão
  { name: 'Print A4', width: 2480, height: 3508, quality: 1.0, format: 'tiff', description: 'A4 300 DPI' },
  { name: 'Print Poster', width: 3508, height: 4961, quality: 1.0, format: 'tiff', description: 'Poster A3 300 DPI' },
  
  // Dispositivos
  { name: 'Desktop Wallpaper', width: 1920, height: 1080, quality: 0.95, format: 'jpeg', description: 'Papel de parede HD' },
  { name: 'Mobile Wallpaper', width: 1080, height: 1920, quality: 0.9, format: 'jpeg', description: 'Papel de parede mobile' },
  { name: 'Tablet Wallpaper', width: 1536, height: 2048, quality: 0.9, format: 'jpeg', description: 'Papel de parede tablet' },
  
  // Ícones
  { name: 'App Icon', width: 512, height: 512, quality: 1.0, format: 'png', description: 'Ícone de aplicativo' },
  { name: 'Favicon', width: 32, height: 32, quality: 1.0, format: 'ico', description: 'Ícone do site' },
  { name: 'Windows Icon', width: 256, height: 256, quality: 1.0, format: 'ico', description: 'Ícone Windows' }
];

// Lista de formatos que suportam remoção de fundo
export const BACKGROUND_REMOVAL_FORMATS = [
  'png', 'webp', 'svg', 'gif', 'tiff', 'tif', 'heic', 'heif'
] as const;

export interface CopywriterResult {
  title: string;
  features: string[];
  description: string;
  benefits: string[];
  triggers: string[];
}

export default {
  SUPPORTED_IMAGE_FORMATS,
  SUPPORTED_VIDEO_FORMATS,
  SUPPORTED_DOCUMENT_FORMATS,
  FORMAT_CATEGORIES,
  FORMAT_DESCRIPTIONS,
  QUALITY_PRESETS,
  CONVERSION_PRESETS,
  validateFileType,
  getFileCategory,
  formatFileSize,
  calculateCompressionRatio
};